import * as React from "react";
import { CheckIcon, ChevronsUpDown } from "lucide-react";
import * as RPNInput from "react-phone-number-input";
import flags from "react-phone-number-input/flags";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface StringMap {
  [key: string]: string;
}



type PhoneInputProps = Omit<React.ComponentProps<"input">, "onChange" | "value" | "ref"> &
  Omit<RPNInput.Props<typeof RPNInput.default>, "onChange"> & {
    onChange?: (value: RPNInput.Value) => void
  }

const PhoneInput: React.ForwardRefExoticComponent<PhoneInputProps> = React.forwardRef<
  HTMLInputElement,
  PhoneInputProps
>(({ className, onChange, value, ...props }, ref) => {
  const [selectedCountry, setSelectedCountry] = React.useState<RPNInput.Country>("MA")
  const [displayValue, setDisplayValue] = React.useState("")

  // Get the dial code for the selected country
  const getDialCode = (country: RPNInput.Country) => {
    return country ? `+${RPNInput.getCountryCallingCode(country)}` : ""
  }

  // Handle country change
  const handleCountryChange = (country: RPNInput.Country) => {
    setSelectedCountry(country)
    const newDialCode = getDialCode(country)
    setDisplayValue(newDialCode)
    // Clear the input value when country changes
    onChange?.("" as RPNInput.Value)
  }

  // Update display value when the actual value changes
  React.useEffect(() => {
    const dialCode = getDialCode(selectedCountry)
    if (value) {
      // If there's a value, we need to ensure the dial code is still at the beginning
      if (!String(value).startsWith(dialCode)) {
        setDisplayValue(`${dialCode}${value}`)
      } else {
        setDisplayValue(String(value))
      }
    } else {
      // If no value, just show the dial code
      setDisplayValue(dialCode)
    }
  }, [value, selectedCountry])

  // Custom input change handler
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const dialCode = getDialCode(selectedCountry)
    const newDisplayValue = e.target.value

    // Ensure dial code is preserved
    if (!newDisplayValue.startsWith(dialCode)) {
      // If user tried to modify the dial code, reset it
      setDisplayValue(dialCode)
      return
    }

    // Extract the actual phone number (without dial code)
    const actualNumber = newDisplayValue.substring(dialCode.length)

    // Update the display value
    setDisplayValue(newDisplayValue)

    // Call the original onChange with just the phone number part
    if (onChange) {
      onChange(actualNumber ? (`${dialCode}${actualNumber}` as RPNInput.Value) : ("" as RPNInput.Value))
    }
  }

  // Handle key down to prevent backspace from deleting the dial code
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const dialCode = getDialCode(selectedCountry)
    const input = e.currentTarget
    const selectionStart = input.selectionStart || 0

    // Prevent backspace from deleting the dial code
    if (e.key === "Backspace" && selectionStart <= dialCode.length) {
      e.preventDefault()
    }

    // Prevent user from placing cursor before the dial code
    if (e.key === "ArrowLeft" && selectionStart <= dialCode.length) {
      e.preventDefault()
      input.setSelectionRange(dialCode.length, dialCode.length)
    }

    // Prevent selection that includes the dial code
    if (e.shiftKey && e.key === "ArrowLeft" && selectionStart <= dialCode.length) {
      e.preventDefault()
    }
  }

  // Handle click to prevent cursor from being placed before the dial code
  const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
    const dialCode = getDialCode(selectedCountry)
    const input = e.currentTarget
    const selectionStart = input.selectionStart || 0

    if (selectionStart < dialCode.length) {
      input.setSelectionRange(dialCode.length, dialCode.length)
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { size: _size, ...inputProps } = props

  return (
    <div className="flex col-span-4" dir="ltr">
      <CountrySelect
        disabled={props.disabled}
        value={selectedCountry}
        options={RPNInput.getCountries().map((country) => ({ label: country, value: country }))}
        onChange={handleCountryChange}
      />
      <Input
        {...inputProps}
        ref={ref}
        type="tel"
        className={cn("rounded-l-none rounded-r-lg -ml-px", className)}
        value={displayValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onClick={handleClick}
        disabled={props.disabled}
        placeholder={props.placeholder}
        dir='ltr'
      />
    </div>
  )
})
PhoneInput.displayName = "PhoneInput"

type CountryEntry = { label: string; value: RPNInput.Country | undefined }

type CountrySelectProps = {
  disabled?: boolean
  value: RPNInput.Country
  options: CountryEntry[]
  onChange: (country: RPNInput.Country) => void
}

// Update the CountrySelect component to handle missing translations
const CountrySelect = ({ disabled, value: selectedCountry, options: countryList, onChange }: CountrySelectProps) => {
  const i18n = useTranslation()
  const [countries, setCountries] = React.useState<StringMap>({})
  const [open, setOpen] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(true)

  React.useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      try {
        const response = await fetch(`/countries_${i18n.i18n.language}.json`)
        if (!response.ok) throw new Error('Failed to load countries')
        const data: StringMap = await response.json()
        setCountries(data)
      } catch (error) {
        console.warn('Could not load country names, falling back to codes:', error)
        setCountries({})
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [i18n.i18n.language])

  // Handle country selection
  const handleCountrySelect = (country: RPNInput.Country) => {
    onChange(country)
    setOpen(false)
  }

  // Get country name with fallback
  const getCountryName = (country: RPNInput.Country): string => {
    return countries[country] || country
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverContent className="w-[300px] p-0" dir={i18n.i18n.dir()}>
        <Command>
          <CommandInput placeholder={i18n.t("search_country")} />
          <CommandList>
            <ScrollArea className="h-72">
              <CommandEmpty>{i18n.t("no_results")}</CommandEmpty>
              {isLoading ? (
                <div className="py-6 text-center text-sm text-muted-foreground">{i18n.t("loading")}</div>
              ) : (
                <CommandGroup>
                  {countryList.filter(c => c.label != 'EH' && c.label != 'IL').map(({ value }) =>
                    value ? (
                      <CountrySelectOption
                        key={value}
                        country={value}
                        countryName={getCountryName(value)}
                        selectedCountry={selectedCountry}
                        onChange={handleCountrySelect}
                      />
                    ) : null,
                  )}
                </CommandGroup>
              )}
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="flex h-9 gap-3 rounded-l-lg rounded-r-none border-r-0 px-3 focus:z-10"
          disabled={disabled}
        >
          <FlagComponent country={selectedCountry} countryName={selectedCountry} />
          <ChevronsUpDown className={cn("-mr-2 size-4 opacity-50", disabled ? "hidden" : "opacity-100")} />
        </Button>
      </PopoverTrigger>
    </Popover>
  )
}

interface CountrySelectOptionProps extends RPNInput.FlagProps {
  selectedCountry: RPNInput.Country
  onChange: (country: RPNInput.Country) => void
}

const CountrySelectOption = ({ country, countryName, selectedCountry, onChange }: CountrySelectOptionProps) => {
  return (
    <CommandItem className="gap-2" onSelect={() => onChange(country)}>
      <FlagComponent country={country} countryName={countryName} />
      <span className="flex-1 text-sm">{countryName}</span>
      <span className="text-sm text-foreground/50">{`+${RPNInput.getCountryCallingCode(country)}`}</span>
      <CheckIcon className={`ml-auto size-4 ${country === selectedCountry ? "opacity-100" : "opacity-0"}`} />
    </CommandItem>
  )
}

const FlagComponent = ({ country, countryName }: RPNInput.FlagProps) => {
  const Flag = flags[country]

  return (
    <span className="flex h-4 w-6 overflow-hidden rounded-sm bg-foreground/20 [&_svg]:size-full">
      {Flag && <Flag title={countryName} />}
    </span>
  )
}

export { PhoneInput }